<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import horarioUsoAPI from 'dashboard/api/horarioUso';
import { useAlert } from 'dashboard/composables';

// Blocos por papel, na ordem de exibicao.
const GROUP_LABELS = {
  administrator: 'Administradores',
  supervisor: 'Supervisores',
  operacional: 'Operacional',
  agent: 'Agentes',
};
const GROUP_ORDER = ['administrator', 'supervisor', 'operacional', 'agent'];
// Papeis com horario configuravel (administrador e isento de horario).
const HORARIO_ROLES = ['supervisor', 'operacional', 'agent'];

const isLoading = ref(true);
const isSaving = ref(false);
const loadError = ref('');
const dataHoje = ref('');
const selectedDate = ref('');
const grupos = ref({});
const defaults = ref({ open: '08:00', close: '19:00' });
const configDia = ref(null); // config completa do dia salvo
const selected = ref(new Set()); // ids liberados (verde = pode logar)

// Horarios editaveis (reactive p/ v-model em campos aninhados).
const state = reactive({
  padrao: {}, // { role: { open, close } }  -> vale todo dia
  overridePapel: {}, // { role: { open, close } } -> so o dia selecionado
  overrideUsuario: {}, // { id: { open, close } } -> so o dia selecionado
});

const ensureRoleObjects = () => {
  HORARIO_ROLES.forEach(role => {
    if (!state.padrao[role]) state.padrao[role] = { open: '', close: '' };
    if (!state.overridePapel[role])
      state.overridePapel[role] = { open: '', close: '' };
  });
};

const blocks = computed(() =>
  GROUP_ORDER.map(key => ({
    key,
    label: GROUP_LABELS[key],
    items: grupos.value[key] || [],
    temHorario: HORARIO_ROLES.includes(key),
  }))
);

const totalSelecionados = computed(() => selected.value.size);
const isHoje = computed(() => selectedDate.value === dataHoje.value);

const isOn = id => selected.value.has(id);
const toggle = id => {
  const s = new Set(selected.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  selected.value = s;
};
const markAll = items => {
  const s = new Set(selected.value);
  items.forEach(u => s.add(u.id));
  selected.value = s;
};
const unmarkAll = items => {
  const s = new Set(selected.value);
  items.forEach(u => s.delete(u.id));
  selected.value = s;
};

const userOverride = id => {
  if (!state.overrideUsuario[id])
    state.overrideUsuario[id] = { open: '', close: '' };
  return state.overrideUsuario[id];
};

// Define selecao + overrides do dia conforme a data escolhida.
const applySelectionForDate = () => {
  const allIds = GROUP_ORDER.flatMap(k => (grupos.value[k] || []).map(u => u.id));
  state.overridePapel = {};
  state.overrideUsuario = {};
  ensureRoleObjects();

  if (configDia.value && configDia.value.data === selectedDate.value) {
    selected.value = new Set(
      (configDia.value.liberados || []).map(n => Number(n))
    );
    const hp = configDia.value.horarios_papel || {};
    HORARIO_ROLES.forEach(role => {
      if (hp[role]) state.overridePapel[role] = { ...hp[role] };
    });
    const hu = configDia.value.horarios_usuario || {};
    Object.keys(hu).forEach(id => {
      state.overrideUsuario[id] = { ...hu[id] };
    });
  } else {
    // dia sem config -> todos liberados, sem override (usa o padrao)
    selected.value = new Set(allIds);
  }
};

const onDateChange = () => applySelectionForDate();

const load = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await horarioUsoAPI.fetch();
    dataHoje.value = data.data_hoje;
    grupos.value = data.grupos || {};
    defaults.value = data.defaults || defaults.value;
    configDia.value = data.config_dia || null;
    selectedDate.value = selectedDate.value || data.data_hoje;

    // padrao: usa o salvo; se faltar papel, pre-preenche com o default (08-19)
    const hp = data.horario_padrao || {};
    state.padrao = {};
    HORARIO_ROLES.forEach(role => {
      state.padrao[role] = hp[role]
        ? { open: hp[role].open || '', close: hp[role].close || '' }
        : { open: defaults.value.open, close: defaults.value.close };
    });

    applySelectionForDate();
  } catch (err) {
    loadError.value =
      err.response?.status === 403
        ? 'Acesso restrito ao administrador principal.'
        : err.response?.data?.error || 'Erro ao carregar.';
  } finally {
    isLoading.value = false;
  }
};

// Mantem so janelas com abertura E fechamento preenchidos.
const pickWindows = obj => {
  const out = {};
  Object.keys(obj || {}).forEach(key => {
    const w = obj[key] || {};
    const open = (w.open || '').trim();
    const close = (w.close || '').trim();
    if (open && close) out[key] = { open, close };
  });
  return out;
};

const save = async () => {
  isSaving.value = true;
  try {
    const payload = {
      data: selectedDate.value,
      liberados: Array.from(selected.value),
      horario_padrao: pickWindows(state.padrao),
      horarios_papel: pickWindows(state.overridePapel),
      horarios_usuario: pickWindows(state.overrideUsuario),
    };
    const { data } = await horarioUsoAPI.save(payload);
    // recarrega para refletir o que foi persistido
    await load();
    const msg =
      data.derrubados > 0
        ? `Salvo. ${data.derrubados} usuário(s) fora do horário/liberação foram deslogados.`
        : 'Horários salvos.';
    useAlert(msg);
  } catch (err) {
    useAlert(err.response?.data?.error || 'Erro ao salvar.');
  } finally {
    isSaving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col h-full w-full overflow-auto bg-n-background p-6 gap-5">
    <header class="flex flex-col gap-1">
      <h1 class="text-2xl font-semibold text-n-slate-12">Horário de Uso</h1>
      <p class="text-sm text-n-slate-11 max-w-3xl">
        Defina o <strong>horário padrão</strong> de cada papel (vale todos os
        dias) e, se precisar, um <strong>ajuste só para o dia escolhido</strong>
        — por papel ou por usuário. Fora do horário, o usuário não consegue
        logar e, se já estiver logado, é desconectado automaticamente. O
        administrador principal nunca é afetado.
      </p>
    </header>

    <div v-if="isLoading" class="text-n-slate-11">Carregando...</div>
    <div v-else-if="loadError" class="text-n-ruby-11 font-medium">
      {{ loadError }}
    </div>

    <template v-else>
      <!-- HORARIO PADRAO (vale todo dia) -->
      <section
        class="flex flex-col gap-3 rounded-xl border border-n-weak p-4 bg-n-solid-1"
      >
        <h2 class="text-lg font-semibold text-n-slate-12">
          Horário padrão
          <span class="text-sm font-normal text-n-slate-10">
            (vale todos os dias)
          </span>
        </h2>
        <div
          v-for="role in HORARIO_ROLES"
          :key="`padrao-${role}`"
          class="flex items-center gap-3 flex-wrap"
        >
          <span class="w-32 text-sm font-medium text-n-slate-12">
            {{ GROUP_LABELS[role] }}
          </span>
          <label class="text-sm text-n-slate-11">das</label>
          <input
            v-model="state.padrao[role].open"
            type="time"
            class="px-2 py-1 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
          <label class="text-sm text-n-slate-11">às</label>
          <input
            v-model="state.padrao[role].close"
            type="time"
            class="px-2 py-1 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
        </div>
      </section>

      <!-- AJUSTE DO DIA -->
      <div class="flex items-center gap-3 flex-wrap">
        <label class="text-sm font-medium text-n-slate-12">
          Ajuste para o dia:
        </label>
        <input
          v-model="selectedDate"
          type="date"
          class="px-3 py-1.5 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          @change="onDateChange"
        />
        <span
          v-if="isHoje"
          class="text-xs px-2 py-0.5 rounded-full bg-n-teal-9 text-white"
        >
          hoje
        </span>
        <span class="text-sm text-n-slate-11">
          Liberados: <strong>{{ totalSelecionados }}</strong>
        </span>
      </div>
      <p class="text-xs text-n-slate-10 -mt-3">
        Campos de horário em branco usam o horário padrão acima. O ajuste vale
        somente para a data selecionada.
      </p>

      <section
        v-for="block in blocks"
        :key="block.key"
        class="flex flex-col gap-3 rounded-xl border border-n-weak p-4 bg-n-solid-1"
      >
        <div class="flex items-center justify-between gap-2 flex-wrap">
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ block.label }}
            <span class="text-sm font-normal text-n-slate-10">
              ({{ block.items.length }})
            </span>
          </h2>
          <div class="flex items-center gap-2 flex-wrap">
            <template v-if="block.temHorario">
              <span class="text-xs text-n-slate-10">horário do papel hoje:</span>
              <input
                v-model="state.overridePapel[block.key].open"
                type="time"
                class="px-2 py-1 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
              <span class="text-xs text-n-slate-10">às</span>
              <input
                v-model="state.overridePapel[block.key].close"
                type="time"
                class="px-2 py-1 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
            </template>
            <button
              class="px-3 py-1.5 text-sm rounded-lg bg-n-teal-9 text-white hover:bg-n-teal-10 disabled:opacity-50"
              :disabled="!block.items.length"
              @click="markAll(block.items)"
            >
              Todos
            </button>
            <button
              class="px-3 py-1.5 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12 border border-n-weak hover:bg-n-alpha-black3 disabled:opacity-50"
              :disabled="!block.items.length"
              @click="unmarkAll(block.items)"
            >
              Desmarcar todos
            </button>
          </div>
        </div>

        <p v-if="!block.items.length" class="text-sm text-n-slate-10">
          Nenhum usuário neste grupo.
        </p>

        <div
          v-else
          class="grid gap-3"
          style="grid-template-columns: repeat(auto-fill, minmax(240px, 1fr))"
        >
          <div
            v-for="user in block.items"
            :key="user.id"
            class="flex flex-col gap-2 rounded-lg border p-3 transition-colors"
            :class="
              isOn(user.id)
                ? 'bg-n-teal-9 border-n-teal-10 text-white'
                : 'bg-n-alpha-black2 border-n-weak text-n-slate-12'
            "
          >
            <button
              type="button"
              class="flex flex-col items-start text-left gap-0.5"
              @click="toggle(user.id)"
            >
              <span class="font-medium truncate w-full">{{ user.name }}</span>
              <span
                class="text-xs truncate w-full"
                :class="isOn(user.id) ? 'text-white/80' : 'text-n-slate-10'"
              >
                {{ user.email }}
              </span>
            </button>
            <div
              v-if="block.temHorario"
              class="flex items-center gap-1 flex-wrap"
              :class="isOn(user.id) ? 'text-white/90' : 'text-n-slate-11'"
            >
              <span class="text-xs">hoje:</span>
              <input
                v-model="userOverride(user.id).open"
                type="time"
                class="px-1.5 py-0.5 text-xs rounded border border-n-weak bg-n-alpha-black2 text-n-slate-12 w-24"
                @click.stop
              />
              <span class="text-xs">às</span>
              <input
                v-model="userOverride(user.id).close"
                type="time"
                class="px-1.5 py-0.5 text-xs rounded border border-n-weak bg-n-alpha-black2 text-n-slate-12 w-24"
                @click.stop
              />
            </div>
          </div>
        </div>
      </section>

      <div class="sticky bottom-0 py-3 bg-n-background">
        <button
          class="px-5 py-2.5 rounded-lg bg-n-teal-9 text-white font-medium hover:bg-n-teal-10 disabled:opacity-50"
          :disabled="isSaving"
          @click="save"
        >
          {{ isSaving ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </template>
  </div>
</template>
